import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import PaginationComponent from '../components/PaginationComponent';
import GiftCardIssueModal from '../components/GiftCardIssueModal';
import GiftCardCodesModal from '../components/GiftCardCodesModal';
import { validateSession } from '../utils/functions';
import { convertToMoney } from '../utils/formats';
import { scheduleDebouncedCallback, clearDebouncedCallback } from '../utils/pagination';
import { listar, detalle, canjear, canjearRepartido, anular } from '../utils/giftCardsApi';
import {
    FILTROS_ESTADO, FILTROS_ORIGEN, ORIGENES, etiquetaEstado, esUtilizable, fechaHora, textoVencimiento, formatearCodigo,
    repartoIgual, errorDeReparto, REPARTO_MIN, REPARTO_MAX,
} from '../utils/giftCards';

// Gift cards (FR-080 … FR-084): listado paginado en el servidor con filtro por estado y búsqueda
// por código (cualquier fragmento) o por número de venta. El detalle muestra el código completo
// (FR-082), las dos ventas y quién hizo cada cambio de estado (FR-081), y desde ahí se canjea a
// saldo a favor (FR-050) o se anula (FR-070) una tarjeta activa.
//
// Premios y cortesías (backend docs/spec-premios-cortesias.md): "Emitir premio o cortesía" crea
// tarjetas sin venta (FR-001), el listado filtra por origen (FR-006) y el canje se puede repartir
// entre varias cédulas, también el de una tarjeta vendida (FR-005).

const MODAL = { display: 'block', background: 'rgba(0,0,0,.4)', overflowY: 'auto' };
const CODIGO = {
    fontFamily: 'SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    fontWeight: 700,
    letterSpacing: '0.08em',
};
const pesos = (n) => (n == null || n === '' ? '—' : `$ ${convertToMoney(Number(n) || 0)}`);
// Lo que vale una tarjeta: plata, o qué cubre si es un voucher de producto o de mesa.
const valorDe = (card) => (card.kind && card.kind !== 'monto' ? (card.benefit || card.label || '—') : pesos(card.amount));

class GiftCardsPage extends Component {

    constructor() {
        super();
        this.state = {
            checked: false, role: 0,
            items: [], total: 0, page: 1, limit: 25,
            status: '', origin: '', search: '', searchInput: '',
            // Emisión de premios y cortesías, y los códigos recién emitidos para copiar.
            emitiendo: false, emitidas: null,
            cargando: true, error: null,
            // Detalle
            seleccionada: null, detalleCargando: false, detalleError: null,
            accion: null, // 'canjear' | 'anular'
            cedula: '', motivo: '', enviando: false, accionError: null, aviso: null,
            // Canje repartido: null = a una sola cédula; si no, [{ cedula, amount }].
            reparto: null,
        };
    }

    async componentDidMount() {
        const role = await validateSession();
        this.setState({ checked: true, role });
        this.cargar();
    }

    componentWillUnmount() {
        clearDebouncedCallback(this, 'searchDebounceTimeout');
    }

    cargar = async ({ page = this.state.page, status = this.state.status, origin = this.state.origin, search = this.state.search } = {}) => {
        this.setState({ cargando: true, error: null });
        try {
            const data = await listar({ page, limit: this.state.limit, search, status, origin });
            this.setState({
                items: (data && data.items) || [],
                total: (data && data.total) || 0,
                page: (data && data.page) || page,
                status, origin, search, cargando: false,
            });
        } catch (e) {
            this.setState({ error: e.message, cargando: false });
        }
    }

    onSearch = (value) => {
        this.setState({ searchInput: value });
        scheduleDebouncedCallback(this, 'searchDebounceTimeout', () => this.cargar({ page: 1, search: value }));
    }

    abrir = async (id) => {
        this.setState({
            seleccionada: null, detalleCargando: true, detalleError: null,
            accion: null, cedula: '', motivo: '', accionError: null, aviso: null, reparto: null,
        });
        try {
            const card = await detalle(id);
            this.setState({ seleccionada: card, detalleCargando: false });
        } catch (e) {
            this.setState({ detalleError: e.message, detalleCargando: false });
        }
    }

    cerrar = () => {
        this.setState({ seleccionada: null, detalleCargando: false, detalleError: null, accion: null, aviso: null });
    }

    // Repartir: arranca con dos partes iguales; el cajero puede sumar cédulas y tocar los montos.
    empezarReparto = () => {
        const card = this.state.seleccionada;
        this.setState({ reparto: repartoIgual(card.amount, REPARTO_MIN).map((amount) => ({ cedula: '', amount: String(amount) })), accionError: null });
    }

    setParte = (i, campo, valor) => {
        const reparto = this.state.reparto.map((p, j) => (j === i ? { ...p, [campo]: valor } : p));
        this.setState({ reparto, accionError: null });
    }

    agregarParte = () => {
        const reparto = [...this.state.reparto, { cedula: '', amount: '' }];
        this.setState({ reparto });
    }

    quitarParte = (i) => {
        this.setState({ reparto: this.state.reparto.filter((_, j) => j !== i), accionError: null });
    }

    // Vuelve a dividir en partes iguales entre las cédulas que hay (lo que no divide va a la primera).
    partesIguales = () => {
        const montos = repartoIgual(this.state.seleccionada.amount, this.state.reparto.length);
        this.setState({ reparto: this.state.reparto.map((p, i) => ({ ...p, amount: String(montos[i]) })), accionError: null });
    }

    confirmarReparto = async () => {
        const card = this.state.seleccionada;
        const partes = this.state.reparto.map((p) => ({ cedula: String(p.cedula).trim(), amount: Number(p.amount) }));
        const error = errorDeReparto(partes, card.amount);
        if (error) {
            this.setState({ accionError: error });
            return;
        }
        this.setState({ enviando: true, accionError: null });
        try {
            const r = await canjearRepartido(card.code, partes);
            const detalleTexto = ((r && r.splits) || partes).map((p) => `${p.cedula}: ${pesos(p.amount)}`).join(' · ');
            await this.refrescar(card.id, `Se repartió ${pesos(card.amount)} como saldo a favor entre ${partes.length} cédulas: ${detalleTexto}.`);
        } catch (e) {
            this.setState({ enviando: false, accionError: e.message });
        }
    }

    confirmarCanje = async () => {
        const card = this.state.seleccionada;
        const cedula = this.state.cedula.trim();
        if (cedula === '') {
            this.setState({ accionError: 'Ingresá la cédula que recibe el saldo.' });
            return;
        }
        this.setState({ enviando: true, accionError: null });
        try {
            const r = await canjear(card.code, cedula);
            const aviso = `Se acreditaron ${pesos(r && r.amount)} a la cédula ${cedula}. Saldo actual: ${pesos(r && r.balance)}.`;
            await this.refrescar(card.id, aviso);
        } catch (e) {
            // El backend trae el motivo en el mensaje (3311 cédula, 3302 estado de la tarjeta, …).
            this.setState({ enviando: false, accionError: e.message });
        }
    }

    confirmarAnulacion = async () => {
        const card = this.state.seleccionada;
        const motivo = this.state.motivo.trim();
        if (motivo === '') {
            this.setState({ accionError: 'El motivo es obligatorio.' });
            return;
        }
        this.setState({ enviando: true, accionError: null });
        try {
            await anular(card.id, motivo);
            await this.refrescar(card.id, 'La gift card quedó anulada.');
        } catch (e) {
            this.setState({ enviando: false, accionError: e.message });
        }
    }

    // Después de canjear o anular: el detalle y la fila del listado tienen que mostrar el estado nuevo.
    refrescar = async (id, aviso) => {
        let card = this.state.seleccionada;
        try {
            card = await detalle(id);
        } catch (e) {
            // El cambio ya se hizo; si falla la relectura, el listado de abajo lo muestra igual.
        }
        this.setState({ seleccionada: card, enviando: false, accion: null, cedula: '', motivo: '', reparto: null, aviso });
        this.cargar();
    }

    // Después de emitir: se muestran los códigos para copiar y el listado ya los incluye.
    emitidas = (cards) => {
        this.setState({ emitiendo: false, emitidas: cards });
        this.cargar({ page: 1 });
    }

    renderEstado(card) {
        const e = etiquetaEstado(card);
        return <span className={`badge ${e.badge}`}>{e.label}</span>;
    }

    renderVenta(p) {
        if (!p) return '—';
        return `#${p.purchaseId} · ${fechaHora(p.date)}${p.cedula ? ` · cédula ${p.cedula}` : ''}`;
    }

    renderAcciones(card) {
        const { accion, cedula, motivo, enviando, accionError, reparto } = this.state;
        if (!esUtilizable(card)) {
            return (
                <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                    Sólo una gift card activa (y no vencida) se puede canjear o anular.
                </p>
            );
        }
        return (
            <div>
                {accion == null && (
                    <div>
                        {(!card.kind || card.kind === 'monto') ? (
                            <button type="button" className="btn btn-accent mr-2" onClick={() => this.setState({ accion: 'canjear', accionError: null, aviso: null })}>
                                Canjear a saldo a favor
                            </button>
                        ) : (
                            <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                                {card.kind === 'mesa'
                                    ? 'Se usa en la cuenta de la mesa: «+ Voucher de mesa».'
                                    : 'Se usa en el POS de cafetería: Cobro → «Premio o cortesía (voucher)».'}
                            </p>
                        )}
                        <button type="button" className="btn btn-outline-danger" onClick={() => this.setState({ accion: 'anular', accionError: null, aviso: null })}>
                            Anular
                        </button>
                    </div>
                )}
                {accion === 'canjear' && reparto == null && (
                    <div>
                        <p className="mb-2" style={{ fontSize: '0.9rem' }}>
                            Se acredita el monto entero ({pesos(card.amount)}) como saldo a favor de la cédula, y la tarjeta
                            queda canjeada. No genera venta, caja ni factura.
                        </p>
                        <div className="form-group mb-2">
                            <label htmlFor="gift-card-redeem-cedula">Cédula</label>
                            <input type="text" id="gift-card-redeem-cedula" className="form-control" value={cedula} disabled={enviando}
                                onChange={(e) => this.setState({ cedula: e.target.value })} />
                        </div>
                        <button type="button" className="btn btn-accent mr-2" disabled={enviando} onClick={this.confirmarCanje}>
                            {enviando ? 'Canjeando…' : 'Confirmar canje'}
                        </button>
                        <button type="button" className="btn btn-outline-primary mr-2" disabled={enviando} onClick={this.empezarReparto}>
                            Repartir entre varias cédulas
                        </button>
                        <button type="button" className="btn btn-white" disabled={enviando} onClick={() => this.setState({ accion: null, accionError: null })}>Cancelar</button>
                    </div>
                )}
                {accion === 'canjear' && reparto != null && this.renderReparto(card)}
                {accion === 'anular' && (
                    <div>
                        <p className="mb-2" style={{ fontSize: '0.9rem' }}>
                            {card.origin && card.origin !== 'venta'
                                ? 'La tarjeta deja de servir. No hubo venta: no hay nada que devolver.'
                                : 'La tarjeta deja de servir. Devolverle la plata al comprador se hace aparte, como cualquier devolución.'}
                        </p>
                        <div className="form-group mb-2">
                            <label htmlFor="gift-card-cancel-reason">Motivo</label>
                            <textarea id="gift-card-cancel-reason" className="form-control" rows="2" value={motivo} disabled={enviando}
                                onChange={(e) => this.setState({ motivo: e.target.value })} />
                        </div>
                        <button type="button" className="btn btn-danger mr-2" disabled={enviando} onClick={this.confirmarAnulacion}>
                            {enviando ? 'Anulando…' : 'Confirmar anulación'}
                        </button>
                        <button type="button" className="btn btn-white" disabled={enviando} onClick={() => this.setState({ accion: null, accionError: null })}>Cancelar</button>
                    </div>
                )}
                {accionError && <div className="alert alert-danger py-2 mt-2 mb-0">{accionError}</div>}
            </div>
        );
    }

    // Canje repartido (FR-005): una fila por cédula. La suma se ve en vivo contra lo que vale la
    // tarjeta, porque el backend rechaza el reparto entero si no cierra.
    renderReparto(card) {
        const { reparto, enviando } = this.state;
        const suma = reparto.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
        const cierra = suma === Number(card.amount);
        return (
            <div>
                <p className="mb-2" style={{ fontSize: '0.9rem' }}>
                    El monto ({pesos(card.amount)}) se reparte como saldo a favor entre las cédulas, en una sola operación.
                    La tarjeta queda canjeada. No genera venta, caja ni factura.
                </p>
                <table className="table table-sm mb-2">
                    <thead className="bg-light"><tr><th>Cédula</th><th style={{ width: '35%' }}>Monto ($)</th><th></th></tr></thead>
                    <tbody>
                        {reparto.map((p, i) => (
                            <tr key={i}>
                                <td>
                                    <input type="text" className="form-control form-control-sm" aria-label={`Cédula ${i + 1}`} value={p.cedula}
                                        disabled={enviando} onChange={(e) => this.setParte(i, 'cedula', e.target.value)} />
                                </td>
                                <td>
                                    <input type="number" min="1" className="form-control form-control-sm" aria-label={`Monto ${i + 1}`} value={p.amount}
                                        disabled={enviando} onChange={(e) => this.setParte(i, 'amount', e.target.value)} />
                                </td>
                                <td className="text-right">
                                    {reparto.length > REPARTO_MIN && (
                                        <button type="button" className="btn btn-sm btn-white" disabled={enviando} onClick={() => this.quitarParte(i)}>Quitar</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className={`mb-2 ${cierra ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.9rem' }}>
                    Suma: {pesos(suma)} de {pesos(card.amount)}{cierra ? '' : ` · faltan ${pesos(Number(card.amount) - suma)}`}
                </p>
                <div className="mb-2">
                    {reparto.length < REPARTO_MAX && (
                        <button type="button" className="btn btn-sm btn-outline-primary mr-2" disabled={enviando} onClick={this.agregarParte}>Agregar cédula</button>
                    )}
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={enviando} onClick={this.partesIguales}>Partes iguales</button>
                </div>
                <button type="button" className="btn btn-accent mr-2" disabled={enviando} onClick={this.confirmarReparto}>
                    {enviando ? 'Repartiendo…' : 'Confirmar reparto'}
                </button>
                <button type="button" className="btn btn-white" disabled={enviando} onClick={() => this.setState({ reparto: null, accionError: null })}>
                    Volver
                </button>
            </div>
        );
    }

    renderOrigen(card) {
        if (!card.origin || card.origin === 'venta') return card.salePurchaseId != null ? `Venta #${card.salePurchaseId}` : '—';
        return ORIGENES[card.origin] || card.origin;
    }

    renderDetalle() {
        const { seleccionada: card, detalleCargando, detalleError, aviso } = this.state;
        if (!card && !detalleCargando && !detalleError) return null;
        const auditoria = card ? [
            [card.origin && card.origin !== 'venta' ? 'Emitida' : 'Vendida',
                card.createdByName ? `${card.createdByName}${card.createdBy ? ` (${card.createdBy})` : ''}` : card.createdBy, card.createdAt],
            ['Usada', card.usedBy, card.usedAt],
            ['Canjeada', card.redeemedBy, card.redeemedAt],
            ['Anulada', card.cancelledBy, card.cancelledAt],
        ].filter(([, by, at]) => by || at) : [];
        return (
            <div id="modal-gift-card" className="modal" role="dialog" style={MODAL}>
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title">Gift card</h4>
                        </div>
                        <div className="modal-body">
                            {detalleCargando && <div className="text-center">Cargando…</div>}
                            {detalleError && <div className="alert alert-danger">{detalleError}</div>}
                            {card && (
                                <React.Fragment>
                                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                                        <span className="gift-card-code" style={{ ...CODIGO, fontSize: '1.75rem' }}>{card.display || formatearCodigo(card.code)}</span>
                                        <span>{this.renderEstado(card)}</span>
                                    </div>
                                    {aviso && <div className="alert alert-success py-2">{aviso}</div>}
                                    <ul className="mb-3">
                                        {card.kind && card.kind !== 'monto'
                                            ? <li><strong>Vale:</strong> {card.label ? `${card.label} — ` : ''}{card.benefit}</li>
                                            : <li><strong>Monto:</strong> {pesos(card.amount)}</li>}
                                        <li><strong>Vencimiento:</strong> {textoVencimiento(card.validUntil)}</li>
                                        {(!card.origin || card.origin === 'venta') ? (
                                            <li><strong>Venta de origen:</strong> {this.renderVenta(card.salePurchase)}</li>
                                        ) : (
                                            <React.Fragment>
                                                <li><strong>Origen:</strong> {ORIGENES[card.origin]}{card.event ? ` · ${card.event.title}` : ''} (sin venta ni factura)</li>
                                                {card.reason && <li><strong>{card.origin === 'premio' ? 'Premio' : 'Motivo'}:</strong> {card.reason}</li>}
                                            </React.Fragment>
                                        )}
                                        {card.usePurchaseId != null && (
                                            <li><strong>Compra donde se usó:</strong> {card.usePurchase ? this.renderVenta(card.usePurchase) : `#${card.usePurchaseId}`}</li>
                                        )}
                                        {card.usePurchaseId != null && (
                                            <li><strong>Aplicado:</strong> {pesos(card.appliedAmount)} · <strong>Sobrante a saldo a favor:</strong> {pesos(card.leftoverAmount)}</li>
                                        )}
                                        {card.redeemedCedula && <li><strong>Canjeada a la cédula:</strong> {card.redeemedCedula}</li>}
                                        {card.redemptions && card.redemptions.length > 0 && (
                                            <li><strong>Canje repartido:</strong> {card.redemptions.map((r) => `${r.cedula}: ${pesos(r.amount)}`).join(' · ')}</li>
                                        )}
                                        {card.cancelReason && <li><strong>Motivo de la anulación:</strong> {card.cancelReason}</li>}
                                    </ul>
                                    <h6 className="text-uppercase text-muted">Historial</h6>
                                    <table className="table table-sm mb-3">
                                        <thead className="bg-light">
                                            <tr><th>Qué</th><th>Quién</th><th>Cuándo</th></tr>
                                        </thead>
                                        <tbody>
                                            {auditoria.map(([que, by, at]) => (
                                                <tr key={que}><td>{que}</td><td>{by || '—'}</td><td>{fechaHora(at)}</td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {this.renderAcciones(card)}
                                </React.Fragment>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" disabled={this.state.enviando} onClick={this.cerrar}>Cerrar</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderUso(card) {
        if (card.redeemedCedula) return `Canje · cédula ${card.redeemedCedula}`;
        if (card.status === 'canjeada') return 'Canje repartido';
        if (card.usePurchaseId != null) return `Venta #${card.usePurchaseId}`;
        return '—';
    }

    render() {
        if (!this.state.checked) return (<div></div>);
        const { items, total, page, limit, status, searchInput, cargando, error } = this.state;
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={53} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <HeaderComponent title="Gift cards" subtitle="Vendidas, premios y cortesías" />
                            <div className="mb-3">
                                <button type="button" className="btn btn-accent" onClick={() => this.setState({ emitiendo: true })}>
                                    Emitir premio o cortesía
                                </button>
                            </div>

                            <div className="card card-small mb-4">
                                <div className="card-body p-3">
                                    <div className="row">
                                        <div className="col-md-3 mb-2">
                                            <label htmlFor="gift-cards-origin">Origen</label>
                                            <select id="gift-cards-origin" className="custom-select" value={this.state.origin}
                                                onChange={(e) => this.cargar({ page: 1, origin: e.target.value })}>
                                                {FILTROS_ORIGEN.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-3 mb-2">
                                            <label htmlFor="gift-cards-status">Estado</label>
                                            <select id="gift-cards-status" className="custom-select" value={status}
                                                onChange={(e) => this.cargar({ page: 1, status: e.target.value })}>
                                                {FILTROS_ESTADO.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label htmlFor="gift-cards-search">Buscar</label>
                                            <input type="search" id="gift-cards-search" className="form-control"
                                                placeholder="Código (o parte) o número de venta"
                                                value={searchInput} onChange={(e) => this.onSearch(e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {error && <div className="alert alert-danger">{error}</div>}

                            <div className="card card-small mb-4">
                                <div className="card-body p-0">
                                    <table className="table mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Código</th>
                                                <th className="text-right">Vale</th>
                                                <th>Estado</th>
                                                <th>Vencimiento</th>
                                                <th>Origen</th>
                                                <th>Uso / canje</th>
                                                <th>Emitida</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cargando && (
                                                <tr><td colSpan="8" className="text-center text-muted">Cargando…</td></tr>
                                            )}
                                            {!cargando && items.length === 0 && (
                                                <tr><td colSpan="8" className="text-center text-muted">No hay gift cards con ese filtro.</td></tr>
                                            )}
                                            {!cargando && items.map((card) => (
                                                <tr key={card.id}>
                                                    <td style={CODIGO}>{card.display || formatearCodigo(card.code)}</td>
                                                    <td className="text-right">{valorDe(card)}</td>
                                                    <td>{this.renderEstado(card)}</td>
                                                    <td>{card.validUntil ? fechaHora(card.validUntil) : 'No vence'}</td>
                                                    <td>{this.renderOrigen(card)}</td>
                                                    <td>{this.renderUso(card)}</td>
                                                    <td>{fechaHora(card.createdAt)}</td>
                                                    <td className="text-right">
                                                        <button type="button" className="btn btn-sm btn-white" onClick={() => this.abrir(card.id)}>Ver</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {total > 0 && (
                                        <div className="p-3">
                                            <PaginationComponent
                                                totalItems={total}
                                                quantityRows={limit}
                                                currentPage={page}
                                                onPageChange={(p) => this.cargar({ page: p })}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
                {this.renderDetalle()}
                {this.state.emitiendo && (
                    <GiftCardIssueModal onClose={() => this.setState({ emitiendo: false })} onIssued={this.emitidas} />
                )}
                {this.state.emitidas && (
                    <GiftCardCodesModal cards={this.state.emitidas} onClose={() => this.setState({ emitidas: null })} />
                )}
            </div>
        );
    }
}

export default GiftCardsPage;
